import { FiTarget } from 'react-icons/fi';

export const infinitivesData = {
  courseId: 'infinitives',
  courseType: 'grammar',
  title: 'Infinitives',
  icon: FiTarget,
  overview: {
    impact: 'Key for expressing purpose and intention',
    focus: 'Using "to + verb" correctly',
    uses: 'All IELTS sections',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'What Are Infinitives?',
      content: `<p>An infinitive is the base form of a verb, typically preceded by "to": <strong>to go, to learn, to improve</strong>. Infinitives can function as nouns, adjectives, or adverbs.</p>`,
      examples: [
        { label: 'Purpose', text: 'I study hard to succeed.' },
        { label: 'Noun', text: 'To learn is to grow.' },
        { label: 'After adjective', text: 'She was happy to help.' },
      ],
    },
    {
      title: 'Verbs Followed by Infinitives',
      content: `<p>These common verbs are followed by infinitives:</p>`,
      points: [
        'want: "I want to improve my English."',
        'need: "You need to practice more."',
        'decide: "She decided to study abroad."',
        'hope: "We hope to pass the exam."',
        'plan: "They plan to travel next year."',
        'promise: "He promised to help."',
        'agree: "I agree to participate."',
        'refuse: "She refused to answer."',
      ],
    },
    {
      title: 'Infinitives of Purpose',
      content: `<p>Use infinitives to explain why something happens. This is very useful for IELTS Writing Task 2:</p>`,
      examples: [
        { label: 'Purpose', text: 'Governments invest in education to improve literacy.' },
        { label: 'Purpose', text: 'People exercise to stay healthy.' },
        { label: 'In order to', text: 'In order to succeed, you must work hard.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Complete with Infinitives',
      type: 'select',
      instructions: 'Choose the correct form.',
      questions: [
        {
          text: 'She decided ___ the job offer.',
          options: ['to accept', 'accepting', 'accept'],
          answer: 'to accept',
          explanation: '"Decide" is always followed by an infinitive.',
        },
        {
          text: 'I want ___ my English skills.',
          options: ['to improve', 'improving', 'improve'],
          answer: 'to improve',
          explanation: '"Want" takes an infinitive.',
        },
        {
          text: 'He promised ___ on time.',
          options: ['to arrive', 'arriving', 'arrive'],
          answer: 'to arrive',
          explanation: '"Promise" is followed by an infinitive.',
        },
        {
          text: 'They plan ___ a new business.',
          options: ['to start', 'starting', 'start'],
          answer: 'to start',
          explanation: '"Plan" takes an infinitive.',
        },
        {
          text: 'I expect ___ from you soon.',
          options: ['to hear', 'hearing', 'hear'],
          answer: 'to hear',
          explanation: '"Expect" is followed by an infinitive.',
        },
        {
          text: 'She agreed ___ in the project.',
          options: ['to participate', 'participating', 'participate'],
          answer: 'to participate',
          explanation: '"Agree" takes an infinitive.',
        },
      ],
    },
    {
      title: 'Infinitives of Purpose',
      type: 'fill-in',
      instructions: 'Complete with an infinitive to explain purpose.',
      questions: [
        {
          text: 'People exercise ___ healthy. (stay)',
          answer: 'to stay',
          explanation: 'The infinitive explains the purpose of exercising.',
        },
        {
          text: 'I study hard ___ good grades. (get)',
          answer: 'to get',
          explanation: 'The infinitive explains why you study.',
        },
        {
          text: 'Governments invest in education ___ literacy. (improve)',
          answer: 'to improve',
          explanation: 'The infinitive explains the purpose of investment.',
        },
      ],
    },
    {
      title: 'Infinitive or Bare Infinitive?',
      type: 'select',
      instructions: 'Choose the correct form (with or without "to").',
      questions: [
        {
          text: 'I helped her ___ the homework.',
          options: ['do', 'to do'],
          answer: 'do',
          explanation: '"Help" can take bare infinitive (without "to") or "to + infinitive".',
        },
        {
          text: 'They made me ___ again.',
          options: ['wait', 'to wait'],
          answer: 'wait',
          explanation: 'After "make" (causative), use bare infinitive.',
        },
        {
          text: 'She let him ___ early.',
          options: ['leave', 'to leave'],
          answer: 'leave',
          explanation: 'After "let," use bare infinitive.',
        },
        {
          text: 'I would rather ___ at home.',
          options: ['stay', 'to stay'],
          answer: 'stay',
          explanation: 'After "would rather," use bare infinitive.',
        },
      ],
    },
    {
      title: 'Adjective + Infinitive',
      type: 'select',
      instructions: 'Complete the sentence with an infinitive.',
      questions: [
        {
          text: 'I\'m happy ___ you.',
          options: ['to meet', 'meeting', 'meet'],
          answer: 'to meet',
          explanation: 'After adjectives like "happy," use infinitive.',
        },
        {
          text: 'This book is easy ___.',
          options: ['to read', 'reading', 'read'],
          answer: 'to read',
          explanation: 'After "easy/difficult/hard," use infinitive.',
        },
        {
          text: 'She was surprised ___ the news.',
          options: ['to hear', 'hearing', 'hear'],
          answer: 'to hear',
          explanation: 'After emotion adjectives, use infinitive.',
        },
      ],
    },
    {
      title: 'Write Sentences with Infinitives',
      type: 'writing',
      instructions: 'Write sentences expressing purpose using infinitives.',
      questions: [
        {
          text: 'Why do people learn foreign languages?',
          example: 'People learn foreign languages to communicate with others and to improve their career prospects.',
          tip: 'Use infinitives: "to communicate," "to improve."',
        },
        {
          text: 'Why should governments invest in renewable energy?',
          example: 'Governments should invest in renewable energy to reduce pollution and to create sustainable development.',
          tip: 'Use "to + verb" to express the purpose.',
        },
      ],
    },
  ],
  tips: [
    'Use infinitives to express purpose in essays',
    'Memorize verbs that take infinitives',
    '"In order to" can add formality to your writing',
  ],
  dailyPractice: [
    'Write sentences expressing purpose',
    'Practice verb + infinitive combinations',
    'Use infinitives in speaking responses',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Infinitives: The "to + verb" form used to express purpose, after certain verbs (want, decide, hope), and as sentence subjects or objects.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...infinitivesData,
      title: infinitivesData.title + ' (Foundations)',
      overview: {
        ...infinitivesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: infinitivesData.sections.slice(0, Math.max(2, Math.ceil(infinitivesData.sections.length / 2))),
      exercises: infinitivesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...infinitivesData,
      title: infinitivesData.title + ' (Advanced)',
      overview: {
        ...infinitivesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: infinitivesData.sections,
      exercises: infinitivesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(infinitivesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return infinitivesData;
}
